/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/**
 * @fileOverview This file contains the implementation of the Edit Item Modal component for Inventory module
 * @version 1.0.0
 * @since 1.0.0
 * @author Diego Benítez Reyna
 */

import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import customAxios from '../../utils/customAxios';
import { getToast } from '../../utils/toastConfig';
import { GlobalVariables } from '../../globalVar';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  TextField,
  createFilterOptions,
} from '@mui/material';
import '../../scss/Components/_modal.scss';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import CurrencyInput from '../UI/CurrencyInput';
import ImageUploadInput from '../UI/ImageUploadInput';
import { validateAllFields } from '../../utils/validateObjectValues';
import { isValueInArray, trimValue } from '../../utils/generalUtils';

const filter = createFilterOptions();

const EditItemModal = ({ show, setShow, refreshItems, itemData }) => {
  const [formValues, setFormValues] = useState({});
  const [initialItemData, setInitialItemData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const [manufacturerList, setManufacturerList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [currencyList, setCurrencyList] = useState([]);
  const [inventoryTypeList, setInventoryTypeList] = useState([]);
  const [locationList, setLocationList] = useState([]);

  const priorityList = [
    { id: 1, value: 0, name: 'Prioridad 0' },
    { id: 2, value: 1, name: 'Prioridad 1' },
    { id: 3, value: 2, name: 'Prioridad 2' },
  ];

  //#region LOCATION AUTOCOMPLETE
  // Dialog open
  const [open, setOpen] = useState(false);
  const initialDialogValue = useState({
    id: '',
    inventory_type_id: '',
    rack: '',
  });
  // Value to be displayed in the dialog
  const [dialogValue, setDialogValue] = useState(initialDialogValue);

  const handleCloseDialog = () => {
    setDialogValue(initialDialogValue);
    setOpen(false);
  };

  const handleSubmitDialog = async (e) => {
    e.preventDefault();
    try {
      const createLocationRes = await customAxios.post(
        `${GlobalVariables.getURL()}locations/create`,
        {
          inventory_type_id: formValues.inventory_type_id,
          rack: dialogValue.rack,
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setFormValues((prev) => ({
        ...prev,
        location_id: createLocationRes.data.insertId,
        rack: dialogValue.rack,
        default_ubication: dialogValue.rack,
      }));

      await getLocationsList(formValues.inventory_type_id);
    } catch (error) {
      console.log(error);
      getToast({ status: 'error', title: error.response.data.message });
      setFormValues((prev) => ({
        ...prev,
        location_id: '',
        rack: '',
        default_ubication: '',
      }));
    }

    handleCloseDialog();
  };

  //#endregion

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);

    // Validate if equipments array is empty
    if (formValues?.equipments && formValues.equipments.length === 0) {
      setError(true);
      setIsLoading(false);
    } else {
      // Obtain the values that where changed in a key/value object
      const changedObject = validateAllFields(initialItemData, formValues);

      // Check if something changed, if no changes, then just close the modal
      if (Object.entries(changedObject).length === 0) {
        getToast({
          status: 'success',
          title: 'Refacción actualizada correctamente',
        });
        setShow(false);
        refreshItems();
        return;
      }

      const updateData = { ...formValues };
      // Add helpful data to validate in the backend
      updateData.initial_location_id = initialItemData.location_id;
      updateData.initial_final_amount = initialItemData.final_amount;
      // First delete equipments property so we can validate if those changed (This is because the formValues.equipments is initially added, so we need to delete it to validate again if it changed)
      delete updateData.equipments;
      // If changedObject contains equipments, then add them to the update data
      if (changedObject?.equipments) {
        updateData.equipments = JSON.stringify(formValues.equipments);
      }

      const formData = new FormData();

      // Populate the formData
      for (const key in updateData) {
        if (updateData.hasOwnProperty(key)) {
          formData.append(key, updateData[key]);
        }
      }

      try {
        // Send formData to update item/separated_inventory/equipments_items
        const updateRes = await customAxios.post(
          `${GlobalVariables.getURL()}inventory/updateItem/${
            initialItemData.separated_inventory_id
          }/${initialItemData.item_id}`,
          formData,
          {
            headers: {
              Authorization: 'bearer ' + localStorage.getItem('token'),
            },
          }
        );

        getToast({
          status: 'success',
          title: 'Refacción actualizada correctamente',
        });
        setIsLoading(false);
        refreshItems();
        setShow(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
        getToast({ status: 'error', title: error.response.data.message });
      }
    }
  };

  //#region Endpoint calls
  const getItemData = async () => {
    try {
      const itemDataRes = await customAxios.get(
        `${GlobalVariables.getURL()}items/getItemByIdAndLocationId/${
          itemData.item_id
        }/${itemData.location_id}`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );
      const equipments = await getEquipments(itemDataRes.data.data.item_id);
      // Add the equipments array to the formValues and the initialItemData
      const completeData = {
        ...itemDataRes.data.data,
        equipments: [...equipments],
      };
      setFormValues(completeData);
      setInitialItemData(completeData);
    } catch (error) {
      console.log(error);
      setFormValues({});
      setInitialItemData({});
      refreshItems();
      setShow(false);
    }
  };

  const getEquipments = async (itemId) => {
    try {
      const equipmentsRes = await customAxios.get(
        `${GlobalVariables.getURL()}items/getEquipmentsByItemId/${itemId}`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      return equipmentsRes.data.data;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const getManufacturerList = async () => {
    try {
      const manufacturerRes = await customAxios.get(
        `${GlobalVariables.getURL()}manufacturers`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setManufacturerList(manufacturerRes.data.data);
    } catch (error) {
      console.log(error);
      setManufacturerList([]);
    }
  };

  const getEquipmentList = async () => {
    try {
      const equipmentRes = await customAxios.get(
        `${GlobalVariables.getURL()}listEquipments`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setEquipmentList(equipmentRes.data);
    } catch (error) {
      console.log(error);
      setEquipmentList([]);
    }
  };

  const getInventoryTypeList = async () => {
    try {
      const { data: res } = await customAxios.get(
        `${GlobalVariables.getURL()}inventory/getInventoryTypesList`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );
      setInventoryTypeList(res.data);
    } catch (error) {
      console.log(error);
      setInventoryTypeList([]);
    }
  };

  const getCurrencyAndUnitsList = async () => {
    try {
      const { data } = await customAxios.get(
        `${GlobalVariables.getURL()}autofillCurrenciesAndUnits`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setUnitList(data.units);
      setCurrencyList(data.currencies);
    } catch (error) {
      console.log(error);
      setUnitList([]);
      setManufacturerList([]);
    }
  };

  const getLocationsList = async (inventoryTypeId) => {
    if (!inventoryTypeId) return setLocationList([]);
    try {
      const locations = await customAxios.get(
        `${GlobalVariables.getURL()}locations/getLocationsByInventoryType/${inventoryTypeId}`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setLocationList(locations.data.data || []);
    } catch (error) {
      console.log(error);
      setLocationList([]);
    }
  };
  //#endregion

  useEffect(() => {
    if (!show) return; // Only get data when modal is open

    getItemData();
    getManufacturerList();
    getEquipmentList();
    getInventoryTypeList();
    getLocationsList(itemData.inventory_type_id);
    getCurrencyAndUnitsList();
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={() => {
        setError(false);
        refreshItems();
        setShow(false);
      }}
      size='xl'
      aria-labelledby='contained-modal-title-vcenter'
      backdropClassName='modal-backdrop'
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Editar Refacción</Modal.Title>
      </Modal.Header>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        adapterLocale={moment.locale()}
      >
        <Modal.Body>
          <form id='items-form' onSubmit={(e) => handleSubmit(e)}>
            <Divider className='mui-dividers'>
              {'Información de la refacción'}
            </Divider>

            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={4}>
                <ImageUploadInput
                  onFileSelect={(file) =>
                    setFormValues((prev) => ({
                      ...prev,
                      image: file,
                      image_name: file?.name || '',
                      image_url: null,
                    }))
                  }
                  selectedImage={formValues.image_url || null}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <TextField
                      sx={{ width: '100%' }}
                      label={'Código'}
                      type='text'
                      value={formValues.code || ''}
                      onChange={(e) =>
                        setFormValues((prev) => ({
                          ...prev,
                          code: e.target.value,
                        }))
                      }
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      sx={{ width: '100%' }}
                      label={'Nombre'}
                      type='text'
                      value={formValues.item_name || ''}
                      onChange={(e) =>
                        setFormValues((prev) => ({
                          ...prev,
                          item_name: e.target.value,
                        }))
                      }
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      sx={{ width: '100%' }}
                      label={'Modelo'}
                      type='text'
                      value={formValues.model || ''}
                      onChange={(e) =>
                        setFormValues((prev) => ({
                          ...prev,
                          model: e.target.value,
                        }))
                      }
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      disablePortal
                      includeInputInList={false}
                      options={manufacturerList}
                      value={
                        formValues.manufacturer_id
                          ? {
                              id: formValues.manufacturer_id,
                              name: formValues.manufacturer_name,
                            }
                          : { id: '', name: '' }
                      }
                      sx={{ width: '100%' }}
                      getOptionLabel={(manufacturerList) =>
                        manufacturerList.name
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Seleccionar fabricante...'
                          inputProps={{
                            ...params.inputProps,
                          }}
                          required={true}
                        />
                      )}
                      onChange={(e, value) =>
                        setFormValues((prev) => ({
                          ...prev,
                          manufacturer_id: value ? value.id : '',
                          manufacturer_name: value ? value.name : '',
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <FormControl error={error} fullWidth>
                      <Autocomplete
                        disablePortal
                        multiple
                        disableCloseOnSelect
                        includeInputInList={false}
                        options={equipmentList}
                        value={formValues.equipments || []}
                        sx={{ width: '100%' }}
                        getOptionLabel={(equipmentList) =>
                          equipmentList.equip_name
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              icon={
                                <CheckBoxOutlineBlankIcon fontSize='small' />
                              }
                              checkedIcon={<CheckBoxIcon fontSize='small' />}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.equip_name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Seleccionar tipo(s) de equipo...'
                            inputProps={{
                              ...params.inputProps,
                            }}
                          />
                        )}
                        onChange={(e, value) => {
                          const sanitizedValues = value.map((equip) => {
                            return {
                              id: equip.id,
                              equip_name: equip.equip_name,
                            };
                          });

                          setFormValues((prev) => ({
                            ...prev,
                            equipments: sanitizedValues.map((equip) => equip),
                          }));
                        }}
                      />
                      {error && (
                        <FormHelperText>
                          Debe de escoger al menos una opción
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      disablePortal
                      includeInputInList={false}
                      options={priorityList}
                      value={
                        formValues.priority != null
                          ? {
                              ...priorityList.filter(
                                (p) => p.value === formValues.priority
                              )[0],
                            }
                          : { id: '', name: '', value: '' }
                      }
                      sx={{ width: '100%' }}
                      getOptionLabel={(priorityList) => priorityList.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Seleccionar prioridad...'
                          inputProps={{
                            ...params.inputProps,
                          }}
                          required={true}
                        />
                      )}
                      onChange={(e, value) =>
                        setFormValues((prev) => ({
                          ...prev,
                          priority: value ? value.value : null,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <CurrencyInput
                      currencyList={currencyList}
                      cost={formValues.cost}
                      selectedCurrency={
                        formValues.currency_id
                          ? {
                              id: formValues.currency_id,
                              currency_name: formValues.currency_name,
                              currency_symbol: formValues.currency_symbol,
                            }
                          : null
                      }
                      cb={{
                        handleCurrencyChange: (currencyData) =>
                          setFormValues((prev) => ({
                            ...prev,
                            currency_id: currencyData.id,
                            currency_name: currencyData.currency_name,
                            currency_symbol: currencyData.currency_symbol,
                          })),
                        handleCostChange: (cost) =>
                          setFormValues((prev) => ({ ...prev, cost })),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider className='mui-dividers' sx={{ marginTop: 2 }}>
              {'Información de Almacén'}
            </Divider>

            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  disablePortal
                  includeInputInList={false}
                  options={inventoryTypeList}
                  value={
                    formValues.inventory_type_id
                      ? {
                          id: formValues.inventory_type_id,
                          name: formValues.inventory_type_name,
                        }
                      : { id: '', name: '' }
                  }
                  sx={{ width: '100%' }}
                  getOptionLabel={(inventoryTypeList) => inventoryTypeList.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Seleccionar almacén...'
                      inputProps={{
                        ...params.inputProps,
                      }}
                      required={true}
                    />
                  )}
                  onChange={async (e, value) => {
                    setFormValues((prev) => ({
                      ...prev,
                      inventory_type_id: value ? value.id : '',
                      inventory_type_name: value ? value.name : '',
                    }));
                    await getLocationsList(value ? value.id : null);
                    setFormValues((prev) => ({
                      ...prev,
                      location_id: '',
                      default_ubication: '',
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  value={
                    formValues.location_id
                      ? {
                          id: formValues.location_id,
                          rack: formValues.default_ubication,
                        }
                      : { id: '', rack: '' }
                  }
                  onChange={(e, newValue) => {
                    if (typeof newValue === 'string') {
                      setTimeout(() => {
                        setOpen(true);
                        setDialogValue({
                          inventory_type_id: formValues.inventory_type_id,
                          rack: trimValue(newValue),
                        });
                      });
                    } else if (newValue && newValue.inputValue) {
                      setOpen(true);
                      setDialogValue({
                        inventory_type_id: formValues.inventory_type_id,
                        rack: trimValue(newValue.inputValue),
                      });
                    } else {
                      setFormValues((prev) => ({
                        ...prev,
                        location_id: newValue?.id || '',
                        default_ubication: trimValue(newValue?.rack || ''),
                      }));
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const trimedValue = trimValue(params.inputValue);

                    if (
                      params.inputValue !== '' &&
                      !isValueInArray(trimedValue, locationList, 'rack')
                    ) {
                      filtered.push({
                        inputValue: params.inputValue,
                        rack: `Agregar ${trimedValue}`,
                      });
                    }

                    return filtered;
                  }}
                  id='free-solo-dialog-demo'
                  options={locationList}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.inputValue) return option.inputValue;
                    return option.rack;
                  }}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  renderOption={(props, option) => (
                    <li {...props}>{option.rack}</li>
                  )}
                  sx={{ width: '100%' }}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Seleccionar ubicación...'
                      required
                    />
                  )}
                />
                <Dialog open={open} onClose={handleCloseDialog}>
                  <form>
                    <DialogTitle>Agregar nueva ubicación</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        {`No se encontró la ubicación ${dialogValue.rack}. ¿Desea agregarla?`}
                      </DialogContentText>
                      <TextField
                        autoFocus
                        margin='dense'
                        id='rack'
                        value={dialogValue.rack}
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            rack: event.target.value,
                          })
                        }
                        label='# de rack'
                        type='text'
                        variant='standard'
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleCloseDialog}>Cancelar</Button>
                      <Button onClick={handleSubmitDialog}>Agregar</Button>
                    </DialogActions>
                  </form>
                </Dialog>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  disablePortal
                  includeInputInList={false}
                  options={unitList}
                  value={
                    formValues.unit_id
                      ? {
                          id: formValues.unit_id,
                          unit_name: formValues.unit_name,
                        }
                      : { id: '', unit_name: '' }
                  }
                  sx={{ width: '100%' }}
                  getOptionLabel={(unitList) => unitList.unit_name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Unidad de medida...'
                      inputProps={{
                        ...params.inputProps,
                      }}
                      required={true}
                    />
                  )}
                  onChange={(e, value) => {
                    setFormValues((prev) => ({
                      ...prev,
                      unit_id: value ? value.id : '',
                      unit_name: value ? value.unit_name : '',
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  sx={{ width: '100%' }}
                  label={'Stock máximo'}
                  type='number'
                  value={formValues.max_stock || ''}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      max_stock: e.target.value,
                    }))
                  }
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='start'>
                        {formValues.unit_name}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  sx={{ width: '100%' }}
                  label={'Stock mínimo'}
                  type='number'
                  value={formValues.min_stock || ''}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      min_stock: e.target.value,
                    }))
                  }
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='start'>
                        {formValues.unit_name}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  sx={{ width: '100%' }}
                  label={'Stock'}
                  type='number'
                  value={formValues.final_amount || 0}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      final_amount: e.target.value,
                    }))
                  }
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='start'>
                        {formValues.unit_name}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </Modal.Body>
      </LocalizationProvider>
      <Modal.Footer>
        <button
          type='submit'
          form='items-form'
          className='save-modal-button'
          disabled={isLoading}
        >
          Guardar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditItemModal;
